import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import {
  Perm,
  type MovementRow,
  type ProductListItem,
  type StockOnHandRow,
  type Warehouse,
} from '../api/types'
import { formatMoney, formatMoment, formatQty } from '../ui/format'
import {
  Banner, Button, Empty, ErrorBanner, Field, Panel, Row, Select, Spinner, TextInput,
} from '@shop/ui'
import { movementSourceNames, movementTypeNames } from '../ui/purchasing'

/**
 * استعلامُ المخزون — <b>«أين هذا الصنف وكم منه؟»</b>.
 *
 * والرصيدُ من الدفتر لا من جدولِ رصيد: مجموعُ الحركات، والقيمةُ مجموعُ
 * (كلفةٍ × اتّجاه). <b>ولا جدولَ ذاكرةٍ في المخطَّط أصلًا</b> — يُضاف يومَ يقول
 * قياسٌ إنّ الأداء يحتاجه، لا قبله.
 */
export default function StockInquiry() {
  const { can } = useAuth()

  const [term, setTerm] = useState('')
  const [variantId, setVariantId] = useState<string | null>(null)
  const [warehouseId, setWarehouseId] = useState('')
  const [includeZero, setIncludeZero] = useState(false)
  const [openHistory, setOpenHistory] = useState<{ variantId: string; name: string } | null>(null)

  const warehouses = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => api<Warehouse[]>('/api/v1/warehouses'),
  })

  // البحثُ يمرّ بالأصناف أوّلًا: الاسمُ والـSKU والباركود والطراز كلُّها
  // مفاتيحُ الكتالوج، والتطبيعُ العربيُّ يقع هناك بالجداول نفسِها.
  const matches = useQuery({
    queryKey: ['products', term],
    queryFn: () => api<ProductListItem[]>(`/api/v1/products?limit=20&q=${encodeURIComponent(term)}`),
    enabled: term.trim().length > 1 && variantId === null,
    placeholderData: keepPreviousData,
  })

  const stock = useQuery({
    queryKey: ['stock', variantId, warehouseId, includeZero],
    queryFn: () =>
      api<StockOnHandRow[]>(
        `/api/v1/stock/on-hand?includeZero=${includeZero}`
        + (variantId ? `&variantId=${variantId}` : '')
        + (warehouseId ? `&warehouseId=${warehouseId}` : ''),
      ),
    placeholderData: keepPreviousData,
  })

  const maySeeCost = can(Perm.costView)

  return (
    <div className="space-y-4">
      <Panel title="استعلامُ المخزون">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="بحثٌ عن صنف" hint="بالاسم أو الطراز أو الباركود أو الـSKU">
            <TextInput
              value={term}
              onChange={(value) => {
                setTerm(value)
                setVariantId(null)
              }}
              placeholder="اكتب اسمًا أو امسح باركودًا…"
            />
          </Field>

          <Field label="المستودع">
            <Select value={warehouseId} onChange={setWarehouseId}>
              <option value="">كلُّ المستودعات</option>
              {(warehouses.data ?? []).map((w) => (
                <option key={w.id} value={w.id}>{w.nameAr}</option>
              ))}
            </Select>
          </Field>
        </div>

        <label className="mt-3 flex items-center gap-2 text-xs text-ink-soft">
          <input type="checkbox" checked={includeZero} onChange={(e) => setIncludeZero(e.target.checked)} />
          أظهِر ما رصيدُه صفر
        </label>

        {/* اختيارُ الصنف من نتائج البحث — ثمّ يُرشَّح الرصيدُ عليه */}
        {term.trim().length > 1 && variantId === null && (
          <div className="mt-3 rounded-md border border-dashed border-line p-3">
            {matches.isPending && <Spinner />}
            {matches.data?.length === 0 && <Empty>لا أصنافَ مطابقة.</Empty>}

            <ul className="space-y-1">
              {(matches.data ?? []).map((product) => (
                <li key={product.id}>
                  <VariantRows
                    productId={product.id}
                    label={product.nameAr}
                    code={product.code}
                    onPick={(id) => {
                      setVariantId(id)
                      setTerm(product.nameAr)
                    }}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}

        {variantId !== null && (
          <div className="mt-3">
            <Button onClick={() => { setVariantId(null); setTerm('') }}>إلغاءُ الترشيح</Button>
          </div>
        )}
      </Panel>

      <Panel title="الرصيدُ الحاليّ">
        {stock.isPending && <Spinner />}
        {stock.isError && <ErrorBanner error={stock.error} />}
        {stock.data?.length === 0 && <Empty>لا رصيدَ مطابق.</Empty>}

        {(stock.data ?? []).some((row) => row.uncostedMovements > 0) && maySeeCost && (
          <div className="mb-3">
            <Banner tone="warn">
              بعضُ الحركات بلا كلفة، فالقيمةُ المعروضةُ <b>حدٌّ أدنى لا رقمٌ تامّ</b>.
            </Banner>
          </div>
        )}

        <ul className="space-y-2">
          {(stock.data ?? []).map((row) => (
            <li key={`${row.variantId}-${row.warehouseId}`} className="rounded-md border border-line px-3 py-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium">{row.productName}</span>
                <span className="tabular text-xs text-ink-soft" dir="ltr">{row.sku}</span>
              </div>

              <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-soft">
                <span>{row.warehouseName}</span>
                <span className={`tabular ${row.qtyOnHand <= 0 ? 'text-warn' : ''}`}>
                  {formatQty(row.qtyOnHand)}
                </span>

                {maySeeCost && (
                  <>
                    <span className="tabular">{formatMoney(row.stockValue)}</span>
                    {row.avgCost !== null && (
                      <span className="tabular">متوسّطُ الكلفة {formatMoney(row.avgCost)}</span>
                    )}
                    {row.uncostedMovements > 0 && (
                      <span className="text-warn">{row.uncostedMovements} حركةً بلا كلفة</span>
                    )}
                  </>
                )}
              </div>

              <div className="mt-2">
                <Button
                  onClick={() =>
                    setOpenHistory(
                      openHistory?.variantId === row.variantId
                        ? null
                        : { variantId: row.variantId, name: row.productName },
                    )
                  }
                >
                  {openHistory?.variantId === row.variantId ? 'إخفاءُ التاريخ' : 'تاريخُ الحركات'}
                </Button>
              </div>

              {openHistory?.variantId === row.variantId && (
                <div className="mt-2">
                  <MovementHistory variantId={row.variantId} />
                </div>
              )}
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  )
}

/** متغيّراتُ صنفٍ — فالمخزونُ على المتغيّر لا على المنتج (ق-١). */
function VariantRows({
  productId,
  label,
  code,
  onPick,
}: {
  productId: string
  label: string
  code: string
  onPick: (variantId: string) => void
}) {
  const [open, setOpen] = useState(false)

  const product = useQuery({
    queryKey: ['product', productId],
    queryFn: () => api<{ variants: { id: string; sku: string; colour: string | null; capacity: string | null; isActive: boolean }[] }>(
      `/api/v1/products/${productId}`),
    enabled: open,
  })

  return (
    <div>
      <button
        type="button"
        className="w-full rounded-md px-2 py-1 text-start text-sm hover:bg-surface"
        onClick={() => setOpen(!open)}
      >
        {label}
        <span className="tabular mx-2 text-xs text-ink-soft" dir="ltr">{code}</span>
      </button>

      {open && (
        <ul className="mt-1 space-y-1 ps-4">
          {product.isPending && <Spinner />}

          {(product.data?.variants ?? [])
            .filter((variant) => variant.isActive)
            .map((variant) => (
              <li key={variant.id}>
                <button
                  type="button"
                  className="w-full rounded-md px-2 py-1 text-start text-xs hover:bg-surface"
                  onClick={() => onPick(variant.id)}
                >
                  {[variant.colour, variant.capacity].filter(Boolean).join(' · ') || 'الافتراضيّ'}
                  <span className="tabular mx-2 text-ink-soft" dir="ltr">{variant.sku}</span>
                </button>
              </li>
            ))}
        </ul>
      )}
    </div>
  )
}

/**
 * تاريخُ الحركات — <b>ومعه المستندُ الذي ولّدها</b>.
 *
 * ورقمُ الحركة وحدَه لا يكفي: السؤالُ دائمًا «من أين جاءت هذه القطعة؟»،
 * وجوابُه رقمُ الإيصال أو التسوية أو التحويل.
 */
function MovementHistory({ variantId }: { variantId: string }) {
  const { can } = useAuth()
  const [type, setType] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  const movements = useQuery({
    queryKey: ['movements', variantId, type, from, to],
    queryFn: () =>
      api<MovementRow[]>(
        `/api/v1/stock/movements?variantId=${variantId}`
        + (type ? `&movementType=${type}` : '')
        + (from ? `&from=${from}` : '')
        + (to ? `&to=${to}` : ''),
      ),
    placeholderData: keepPreviousData,
  })

  const maySeeCost = can(Perm.costView)

  return (
    <div className="rounded-md border border-line bg-surface p-3">
      <div className="mb-3 grid gap-2 sm:grid-cols-3">
        <Field label="نوعُ الحركة">
          <Select value={type} onChange={setType}>
            <option value="">الكلّ</option>
            {Object.entries(movementTypeNames).map(([code, name]) => (
              <option key={code} value={code}>{name}</option>
            ))}
          </Select>
        </Field>

        <Field label="من تاريخ">
          <TextInput value={from} onChange={setFrom} type="date" dir="ltr" />
        </Field>

        <Field label="إلى تاريخ">
          <TextInput value={to} onChange={setTo} type="date" dir="ltr" />
        </Field>
      </div>

      {movements.isPending && <Spinner />}
      {movements.isError && <ErrorBanner error={movements.error} />}
      {movements.data?.length === 0 && <Empty>لا حركاتٍ مطابقة.</Empty>}

      <ul className="space-y-2">
        {(movements.data ?? []).map((movement) => (
          <li key={movement.id} className="rounded-md border border-line bg-surface-raised px-3 py-2">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-sm">
                {movementTypeNames[movement.movementType] ?? movement.movementType}
                <span className={`tabular mx-2 ${movement.direction === 1 ? 'text-ok' : 'text-warn'}`}>
                  {movement.direction === 1 ? '+' : '−'}{formatQty(movement.qtyBase)}
                </span>
              </span>

              <span className="tabular text-xs text-ink-soft" dir="ltr">{movement.movementNo}</span>
            </div>

            <dl className="mt-1 space-y-0.5 text-xs">
              <Row label="المستودع">{movement.warehouseName}</Row>

              {movement.sourceNo && (
                <Row label="المستند">
                  {movementSourceNames[movement.sourceType ?? ''] ?? movement.sourceType}
                  <span className="tabular mx-1" dir="ltr">{movement.sourceNo}</span>
                </Row>
              )}

              {movement.reasonCode && <Row label="السبب">{movement.reasonCode}</Row>}
              {movement.note && <Row label="ملاحظة">{movement.note}</Row>}

              {maySeeCost && movement.unitCost !== null && (
                <Row label="كلفةُ الوحدة">{formatMoney(movement.unitCost, movement.currency)}</Row>
              )}

              {/* ‏`occurred_at` تاريخُ الوقوع، و`created_at` تاريخُ الإدخال —
                  ويختلفان حين يُدخل الموظّفُ حركةَ أمسِ اليوم */}
              <Row label="وقعت">{formatMoment(movement.occurredAt)}</Row>
              {movement.occurredAt.slice(0, 10) !== movement.createdAt.slice(0, 10) && (
                <Row label="أُدخلت">{formatMoment(movement.createdAt)}</Row>
              )}
            </dl>
          </li>
        ))}
      </ul>
    </div>
  )
}
